/* Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved. */

import { Info } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Text, UnstyledButton } from '@/shared/ui';
import { formatCents } from '@/shared/utils/format-cents';
import type { BillingCycle } from '@features/onboarding/hooks/use-plans';
import type { PreviewRefusal } from '../../hooks/previewRefusal';

/** What the free period caps AI at, until the period ends (DT-014). */
const TRIAL_AI_CAP = 200;

interface Props {
  /** The server said no and why: no figures, no retry. */
  refusal: PreviewRefusal | null;
  failed: boolean;
  loading: boolean;
  onRetry: () => void;
  /** When the free period ends and the first charge happens. */
  conversionDate: string;
  planTitle: string;
  cycle: BillingCycle;
  /** The server's figure for the first charge, null while unknown. */
  nextChargeCents: number | null;
  seats: number;
}

/** The sentence for a refusal the server named. */
function RefusalNotice({ refusal }: { refusal: PreviewRefusal }) {
  const { t } = useTranslation('features/pricing');
  return (
    <div
      role="alert"
      data-testid="plan-flow-refused"
      className="flex items-start gap-(--space-3) rounded-(--radius-lg) border border-(--border-subtle) bg-(--surface-secondary) p-(--space-4)"
    >
      <Text component="span" className="text-(length:--text-body-sm)">
        {t(`planFlow.review.trial.refused.${refusal}`)}
      </Text>
    </div>
  );
}

/** The figure of the first-charge row. */
function firstChargeLabel(
  nextChargeCents: number | null,
  loading: boolean,
  calculating: string,
): string {
  if (nextChargeCents !== null) return formatCents(nextChargeCents);
  return loading ? calculating : '—';
}

/**
 * The billing rows of a change made in the free period.
 *
 * Nothing is charged today (RN-08); the first charge is the conversion date
 * at the price of whatever the plan, cycle and seats are by then (RN-14).
 * The figure is the server's: it is never computed here.
 *
 * @param props - Component props.
 * @returns The rows and the notice, or the refusal.
 */
export function PlanFlowTrialBilling(props: Props) {
  const { t } = useTranslation('features/pricing');
  const { refusal, failed, loading, onRetry, conversionDate, planTitle, cycle, nextChargeCents, seats } = props;
  if (refusal) return <RefusalNotice refusal={refusal} />;

  return (
    <>
      <div
        data-testid="plan-flow-billing"
        className="flex flex-col divide-y divide-(--border-ultra-subtle) rounded-(--radius-lg) border border-(--border-subtle)"
      >
        <div className="flex flex-col gap-(--space-1) p-(--space-4)">
          <div className="flex items-baseline justify-between gap-(--space-3)">
            <Text component="span" className="text-(length:--text-body-sm)">
              {t('planFlow.review.chargedToday')}
            </Text>
            {failed ? (
              <Text component="span" className="text-(length:--text-body-sm) text-(--status-error-text)">
                {t('planFlow.review.previewFailed')}
              </Text>
            ) : (
              <Text component="span" data-testid="plan-flow-charged-today" className="[font-weight:var(--font-bold)]">
                {formatCents(0)}
              </Text>
            )}
          </div>
          {failed && (
            <UnstyledButton
              onClick={onRetry}
              className="self-start text-(length:--text-body-sm) text-(--seed-base) underline"
            >
              {t('planFlow.review.retry')}
            </UnstyledButton>
          )}
        </div>

        <div className="flex flex-col gap-(--space-1) p-(--space-4)">
          <div className="flex items-baseline justify-between gap-(--space-3)">
            <Text component="span" className="text-(length:--text-body-sm)">
              {t('planFlow.review.trial.firstCharge')}
            </Text>
            <Text component="span" data-testid="plan-flow-first-charge" className="[font-weight:var(--font-semibold)]">
              {firstChargeLabel(nextChargeCents, loading, t('planFlow.review.calculating'))}
            </Text>
          </div>
          <Text component="span" className="text-(length:--text-meta) text-(--text-secondary)">
            {t('planFlow.review.trial.firstChargeWhen', {
              date: conversionDate,
              plan: planTitle,
              cycle: t(`planFlow.review.trial.cycle.${cycle}`),
            })}
          </Text>
          {seats > 1 && nextChargeCents !== null && (
            <Text component="span" className="text-(length:--text-meta) text-(--text-secondary)">
              {t('planFlow.review.seatBreakdown', {
                count: seats,
                price: formatCents(nextChargeCents / seats),
              })}
            </Text>
          )}
        </div>
      </div>

      <div
        data-testid="plan-flow-trial-notice"
        className="flex items-start gap-(--space-2) rounded-(--radius-md) border border-(--status-info-border) bg-(--status-info-bg) p-(--space-3)"
      >
        <Info aria-hidden width={16} height={16} className="mt-0.5 shrink-0" />
        <Text component="span" className="text-(length:--text-body-sm)">
          {t('planFlow.review.trial.notice', { date: conversionDate, interactions: TRIAL_AI_CAP })}
        </Text>
      </div>
    </>
  );
}

export default PlanFlowTrialBilling;
